"use client";

// src/components/organisms/legal/hubspot-tracking.tsx
// HubSpot tracking code, gated on the "analytics" cookie category.
//
//   no consent cookie yet     -> implied consent, tag loads (see IMPLIED_CONSENT)
//   analytics rejected        -> nothing loads
//   analytics accepted        -> tag injected once (guarded by the script id, so
//                                client-side navigation never re-injects it)
//   analytics withdrawn later -> _hsq doNotTrack + _hsp revokeCookieConsent
//   analytics accepted again  -> _hsq doNotTrack {track:true} lifts the block
//
// State comes from the consent cookie (survives reload + navigation); live changes
// arrive via CONSENT_CHANGED_EVENT dispatched by <CookieConsentProvider>.

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { siteDetails } from "@/data/siteDetails";
import {
  type AnalyticsConsent,
  CONSENT_CHANGED_EVENT,
  consentAction,
  readAnalyticsConsent,
} from "@/lib/consent";
import {
  loadHubspotTracking,
  resumeHubspotTracking,
  stopHubspotTracking,
  trackHubspotPageView,
} from "@/lib/hubspot";

function currentPath(): string {
  return window.location.pathname + window.location.search;
}

export function HubspotTracking() {
  const [analytics, setAnalytics] = useState<AnalyticsConsent>(null);
  const pathname = usePathname();
  // Sticky: once the tag is on the page it stays. Withdrawal is handled through
  // the _hsq/_hsp queues, not by removing the script.
  const startedRef = useRef(false);
  // Path the last pageview was sent for; the tag sends the load-time one itself.
  const trackedPathRef = useRef<string | null>(null);

  useEffect(() => {
    function sync() {
      setAnalytics(readAnalyticsConsent());
    }
    sync();
    window.addEventListener(CONSENT_CHANGED_EVENT, sync);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    switch (consentAction(analytics, startedRef.current)) {
      case "load":
        startedRef.current = true;
        trackedPathRef.current = currentPath();
        loadHubspotTracking(siteDetails.hubspotPortalId);
        break;
      case "resume":
        resumeHubspotTracking();
        break;
      case "stop":
        stopHubspotTracking();
        break;
    }
  }, [analytics]);

  // App Router navigations don't reload the tag, so later pageviews are manual.
  useEffect(() => {
    if (!startedRef.current || analytics !== true) return;
    const path = currentPath();
    if (path === trackedPathRef.current) return;
    trackedPathRef.current = path;
    trackHubspotPageView(path);
  }, [pathname, analytics]);

  return null;
}
